import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Tag, Modal, message } from 'antd';
import {
  EyeOutlined,
  DeleteOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getWorkflowInstances,
  deleteWorkflowInstance,
  cancelWorkflowInstance,
  type WorkflowInstance,
  type WorkflowInstanceFilters,
} from '@/api/workflowApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  pending: 'default',
  running: 'processing',
  completed: 'green',
  failed: 'red',
  cancelled: 'orange',
};

export default function WorkflowInstanceListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<WorkflowInstanceFilters>({});

  const canWrite = hasPermission('workflows', 'write');
  const canDelete = hasPermission('workflows', 'delete');

  const handleFilterChange = useCallback(
    (key: keyof WorkflowInstanceFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => getWorkflowInstances({ ...params, ...filters }),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleCancel = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('workflows.confirm_cancel'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await cancelWorkflowInstance(id);
            message.success(t('workflows.instance_cancel_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('workflows.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteWorkflowInstance(id);
            message.success(t('workflows.instance_delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<WorkflowInstance> = useMemo(
    () => [
      {
        title: t('workflows.field.definition_name'),
        dataIndex: 'definition_name',
        key: 'definition_name',
        sorter: true,
        ellipsis: true,
        render: (name: string, record: WorkflowInstance) => (
          <a onClick={() => navigate(`/workflows/instances/${record.id}`)}>{name}</a>
        ),
      },
      {
        title: t('workflows.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (status: string) => (
          <Tag color={STATUS_COLORS[status] ?? 'default'}>
            {t(`workflows.instance_status_options.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('workflows.field.current_step_index'),
        dataIndex: 'current_step_index',
        key: 'current_step_index',
        width: 100,
        align: 'center',
      },
      {
        title: t('workflows.field.started_at'),
        dataIndex: 'started_at',
        key: 'started_at',
        width: 170,
        render: (val: string | null) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('workflows.field.completed_at'),
        dataIndex: 'completed_at',
        key: 'completed_at',
        width: 170,
        render: (val: string | null) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('workflows.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 170,
        sorter: true,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 130,
        render: (_: unknown, record: WorkflowInstance) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/workflows/instances/${record.id}`)}
            />
            {canWrite &&
              (record.status === 'pending' || record.status === 'running') && (
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<StopOutlined />}
                  onClick={() => handleCancel(record.id)}
                  title={t('workflows.action.cancel')}
                />
              )}
            {canDelete &&
              (record.status === 'completed' ||
                record.status === 'failed' ||
                record.status === 'cancelled') && (
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                />
              )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleCancel, handleDelete]
  );

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('workflows.filter.all') },
      { value: 'pending', label: t('workflows.instance_status_options.pending') },
      { value: 'running', label: t('workflows.instance_status_options.running') },
      { value: 'completed', label: t('workflows.instance_status_options.completed') },
      { value: 'failed', label: t('workflows.instance_status_options.failed') },
      { value: 'cancelled', label: t('workflows.instance_status_options.cancelled') },
    ],
    [t]
  );

  const filterBar = (
    <Space wrap>
      <Select
        placeholder={t('workflows.field.status')}
        style={{ width: 140 }}
        allowClear
        options={statusOptions}
        onChange={(val) => handleFilterChange('status', val || undefined)}
      />
    </Space>
  );

  return (
    <div>
      <h2>{t('workflows.instances_title')}</h2>
      <DataGrid<WorkflowInstance>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('workflows.search_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={filterBar}
        onRow={(record) => ({
          onDoubleClick: () => navigate(`/workflows/instances/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
